import { Doctor, Hospital } from '@api/GoodocAPI';
import { ReactComponent as DefaultDoctorImage } from '@assets/icons/doctor_avatar_default.svg';
import { useDoctorSelectionContext } from '@context/DoctorSelection';
import {
  FocusableComponentLayout,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import { motion } from 'framer-motion';
import { FC, Fragment, useMemo, useState } from 'react';

import { generateDoctorSelectionFocusKey } from '../../focusKey';
import DetailPanel from '../DetailPanel';
import StatusPanel from '../StatusPanel';

import * as styles from './styles';

type HospitalType = Pick<Hospital, 'id' | 'name'>;
type DoctorType = Pick<Doctor, 'id' | 'name' | 'tvImageUrl' | 'untactOnTreatment'> & {
  hospital: HospitalType;
};

interface DoctorListItemProps {
  index: number;
  doctor: DoctorType | null;
}

const DoctorListItem: FC<DoctorListItemProps> = ({ index, doctor }) => {
  const { isDetailVisible, selectedDoctorIndex, scrollToItem, goDoctorDetail } =
    useDoctorSelectionContext();

  const { ref, focused } = useFocusable({
    focusKey: generateDoctorSelectionFocusKey(index),
    onEnterPress: () => goDoctorDetail(doctor?.id ?? ''),
    onFocus: (layout: FocusableComponentLayout) => scrollToItem(index, layout)
  });

  const [isLoaded, setIsLoaded] = useState(false);
  const onLoad = () => setIsLoaded(true);

  const image = useMemo(() => {
    return (
      <Fragment>
        <img
          css={[styles.image, isLoaded && styles.imageLoaded]}
          src={doctor?.tvImageUrl ?? ''}
          alt={doctor?.name ?? ''}
          onLoad={onLoad}
        />
        {!isLoaded && <DefaultDoctorImage css={styles.defaultImage} />}
      </Fragment>
    );
  }, [doctor?.tvImageUrl, doctor?.name, isLoaded]);

  return (
    <div ref={ref} css={styles.container}>
      {/* `motion.div (framer-motion)` of current selected item will be disabled when `DocterDetailView` is visible */}
      {/* scroll of `motion.div` behind of other view is broken */}
      {isDetailVisible ? (
        index !== selectedDoctorIndex && <div css={styles.imageContainer}>{image}</div>
      ) : (
        <motion.div layoutId={`doctor-image-${index}`} css={styles.imageContainer}>
          {image}
        </motion.div>
      )}
      <div css={[styles.mainContainer, focused && styles.mainContainerFocused]}>
        {focused ? (
          <DetailPanel />
        ) : (
          <StatusPanel untactOnTreatment={doctor?.untactOnTreatment ?? false} />
        )}
      </div>
      <div css={styles.bottomContainer}>
        <div css={styles.name}>{doctor?.name ?? ''}</div>
        <div css={styles.hospitalName}>{doctor?.hospital?.name ?? ''}</div>
      </div>
    </div>
  );
};

export default DoctorListItem;
